import React from 'react';
import { cn } from '../../utils/cn';
import type { ScheduleStrategySummary } from '../../types/schedule';
import { CHIP_DENSE } from '../../utils/chipColors';

export interface StatusBoxCounts {
  open: number;        // position-level OPEN (live legs)
  pending: number;     // entry TRIGGER_PENDING (WAT / ORB awaiting condition)
  openOrders: number;  // order_status = OPEN across entry / SL / exit (unfilled — actionable)
  error: number;       // order_status = REJECTED across entry / SL / exit
  missed?: number;     // scheduled exit missed today (past_exit + stuck_pending). Optional —
                       // upstream sources that don't compute missed-exit detection
                       // (e.g. per-broker drilldowns) leave it absent; the box dims to 0.
  missedTooltip?: string; // optional override for the missed-exit box tooltip (e.g. per-reason
                          // breakdown). Falls back to the generic actionable copy when omitted.
  cancelled: number;   // order_status = CANCELLED across entry / SL / exit (informational)
  closed: number;      // position-level CLOSED
}

type StatusBoxRowProps =
  | { summary: ScheduleStrategySummary; counts?: undefined }
  | { summary?: undefined; counts: StatusBoxCounts };

const BOX_BASE =
  'inline-flex items-center justify-center min-w-[24px] h-5 px-1 rounded text-[10px] font-bold tabular-nums cursor-help';
const DIM = 'opacity-30';

function deriveCounts(summary: ScheduleStrategySummary): StatusBoxCounts {
  const e = summary.entry_counts;
  const sl = summary.sl_counts;
  const x = summary.exit_counts;
  // Missed-exit is today-only; the backend OMITS the field on past-day views
  // so the nullish-coalesce here is enough — no extra ctx.isPast gate needed.
  // Per-reason breakdown becomes the tooltip when available so the trader
  // sees whether it's a past_exit (handler didn't fire) or stuck_pending
  // (broker hung) — actionable signal differs between the two.
  const missed = summary.missed_exit_count ?? 0;
  const reasons = summary.missed_exit_reasons;
  const reasonBits: string[] = [];
  if (reasons?.past_exit) reasonBits.push(`${reasons.past_exit} past exit`);
  if (reasons?.stuck_pending) reasonBits.push(`${reasons.stuck_pending} stuck pending`);
  const missedTooltip = reasonBits.length > 0
    ? `Scheduled exit missed (${reasonBits.join(' · ')}) — needs attention`
    : undefined;
  return {
    open: summary.open_count,
    // TRIGGER_PENDING on entry = WAT/ORB awaiting trigger. SL's TRIGGER_PENDING
    // is the healthy armed state and is NOT counted here. Exit has no
    // TRIGGER_PENDING status.
    pending: e.pending,
    openOrders: e.open + sl.open + x.open,
    error: e.rejected + sl.rejected + x.rejected,
    missed,
    missedTooltip,
    // `sl.cancelled ?? 0` so the UI stays safe on older backend payloads
    // that predate the sl_counts.cancelled split — the Cancelled box just
    // undercounts the SL axis until the trading stack redeploys.
    cancelled: e.cancelled + (sl.cancelled ?? 0) + x.cancelled,
    closed: summary.closed_count,
  };
}

/**
 * Seven colour-coded count boxes per strategy context:
 *   Open (blue) · Pending (yellow) · Open orders (orange) · Error (red) ·
 *   Missed (red) · Cancelled (gray-100) · Closed (gray-200).
 *
 * CANCELLED uses `CHIP_DENSE.CANCELLED` (bg-gray-100, lighter — "informational
 * footnote") and CLOSED uses `CHIP_DENSE.CLOSED` (bg-gray-200, one shade darker
 * — "terminal/archived"). Distinct shades keep the two adjacent boxes visually
 * separable; canonical chip palette enforces both via `chipColors.ts`.
 *
 * Position of each box is stable so a trader can scan for the orange / red
 * columns at a glance. Boxes with count 0 are dimmed — never hidden — to
 * preserve horizontal alignment.
 *
 * Severity reads left→right: live → waiting → stuck → rejected → missed →
 * user-cancelled → done. The split between Open orders (order_status = OPEN —
 * unfilled at broker) and Error (order_status = REJECTED) surfaces the
 * resolution path: OPEN orders typically need cancel/modify, REJECTED orders
 * need retry, and Missed needs operator-driven exit (modal exit or admin
 * force-exit). The Cancelled box is informational — it does not pulse and
 * does not count towards `statusBoxAttentionCount`.
 */
export const StatusBoxRow: React.FC<StatusBoxRowProps> = (props) => {
  const derived = props.summary ? deriveCounts(props.summary) : props.counts;
  const { open, pending, openOrders, error, cancelled, closed } = derived;
  const missed = derived.missed ?? 0;
  const missedTooltip = derived.missedTooltip;

  const boxes = [
    {
      key: 'open',
      cls: CHIP_DENSE.OPEN,
      count: open,
      title: 'Open legs (currently live)',
      blink: false,
    },
    {
      key: 'pending',
      cls: CHIP_DENSE.TRIGGER_PENDING,
      count: pending,
      title: 'Entries armed, awaiting trigger condition (WAT / ORB)',
      blink: false,
    },
    {
      key: 'openOrders',
      cls: CHIP_DENSE.MANUAL_RETRY, // orange semantic for open-orders-need-attention
      count: openOrders,
      title: 'Orders placed but sitting unfilled at broker (OPEN) — needs attention',
      // Pulse the box itself when there are any OPEN orders.
      blink: openOrders > 0 ? 'animate-blink-orange' : false,
    },
    {
      key: 'error',
      cls: CHIP_DENSE.REJECTED,
      count: error,
      title: 'Rejected entry / SL / exit orders',
      blink: error > 0 ? 'animate-blink-red' : false,
    },
    // 7th box: scheduled exit missed today. Reuses the REJECTED red palette
    // because it shares the same severity bucket — actionable, blinks red,
    // counted toward `statusBoxAttentionCount`. Distinct from the Error box:
    // Error is a broker-side reject, Missed is a platform-side exit that
    // never reached the broker (or its order is stuck). Both demand operator
    // intervention; the per-reason tooltip helps the operator triage.
    {
      key: 'missed',
      cls: CHIP_DENSE.REJECTED,
      count: missed,
      title: missedTooltip
        ?? 'Scheduled exit missed today (past exit time or stuck pending) — needs attention',
      blink: missed > 0 ? 'animate-blink-red' : false,
    },
    {
      key: 'cancelled',
      cls: CHIP_DENSE.CANCELLED, // bg-gray-100 — lighter, "informational footnote"
      count: cancelled,
      title: 'Cancelled entry / SL / exit orders (informational)',
      blink: false,
    },
    {
      key: 'closed',
      cls: CHIP_DENSE.CLOSED, // bg-gray-200 — one shade darker than CANCELLED so adjacent boxes stay visibly distinct
      count: closed,
      title: 'Legs that have exited',
      blink: false,
    },
  ];

  return (
    <div className="inline-flex items-center gap-1">
      {boxes.map(({ key, cls, count, title, blink }) => (
        <span
          key={key}
          title={title}
          className={cn(BOX_BASE, cls, count === 0 && DIM, blink || undefined)}
        >
          {count}
        </span>
      ))}
    </div>
  );
};

/**
 * Count of actionable problems (OPEN unfilled + REJECTED + missed-exit)
 * across a strategy. Used by row-blink gates in Schedule / Positions tables.
 *
 * Missed-exit joins the actionable set because the trader's intervention is
 * required to recover (manual exit via the modal or admin force-exit) —
 * the platform will not self-heal a past_exit / stuck_pending state.
 */
export function statusBoxAttentionCount(
  input: ScheduleStrategySummary | StatusBoxCounts,
): number {
  if ('entry_counts' in input) {
    const c = deriveCounts(input);
    return c.openOrders + c.error + (c.missed ?? 0);
  }
  return input.openOrders + input.error + (input.missed ?? 0);
}

export default StatusBoxRow;
